import { HttpMethodPattern } from './url-match';

export interface MockDef {
  id: string;
  enabled: boolean;
  method: HttpMethodPattern;
  urlPattern: string;
  status: number;
  delayMs?: number;
  body: string;
}

const STORAGE_KEY = 'msw:mocks';

export class MockRegistry {
  private mocks: MockDef[] = [];

  constructor(
    private readonly storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
    private readonly idFactory: () => string = MockRegistry.defaultIdFactory(),
  ) {
    const raw = this.storage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        this.mocks = JSON.parse(raw) as MockDef[];
      } catch {
        this.mocks = [];
      }
    }
  }

  private static defaultIdFactory(): () => string {
    let n = 0;
    return () => `mock-${++n}`;
  }

  list(): MockDef[] {
    return this.mocks;
  }

  add(def: Omit<MockDef, 'id'>): MockDef {
    const created: MockDef = { ...def, id: this.idFactory() };
    this.mocks = [...this.mocks, created];
    this.persist();
    return created;
  }

  update(id: string, patch: Partial<Omit<MockDef, 'id'>>): void {
    this.mocks = this.mocks.map(m => (m.id === id ? { ...m, ...patch } : m));
    this.persist();
  }

  remove(id: string): void {
    this.mocks = this.mocks.filter(m => m.id !== id);
    this.persist();
  }

  toggle(id: string): void {
    this.mocks = this.mocks.map(m => (m.id === id ? { ...m, enabled: !m.enabled } : m));
    this.persist();
  }

  private persist(): void {
    this.storage.setItem(STORAGE_KEY, JSON.stringify(this.mocks));
  }
}
